"use client";

import Toast, {
  TOAST_DURATION,
  ToastButtonProps,
  ToastProps,
} from "@/components/ui/toast/Toast";
import SimpleToast from "@/components/ui/toast/SimpleToast";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";

export enum ToastType {
  SUCCESS = "success",
  ERROR = "error",
  INFO = "info",
  WARNING = "warning",
  SIMPLE = "simple",
}

export interface ShowToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  buttonProps?: ToastButtonProps;
}

interface ToastContextType {
  showToast: (props: ShowToastProps) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<
    {
      id: number;
      message: ToastProps["message"];
      type: ToastProps["type"];
      duration: ToastProps["duration"];
      buttonProps?: ToastProps["buttonProps"];
    }[]
  >([]);

  const showToast = useCallback(
    ({
      message,
      type = ToastType.ERROR,
      duration = TOAST_DURATION,
      buttonProps,
    }: ShowToastProps) => {
      const id = Date.now();
      setToasts((prevToasts) => [
        ...prevToasts,
        { id, message, type, duration, buttonProps },
      ]);

      setTimeout(() => {
        setToasts((prevToasts) =>
          prevToasts.filter((toast) => toast.id !== id)
        );
      }, duration + 500); // +500ms for fade-out animation
    },
    []
  );

  useEffect(() => {
    window.__toast = { showToast };
    return () => {
      delete window.__toast;
    };
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.map(({ id, message, type, duration, buttonProps }) => {
        const handleClose = () =>
          setToasts(toasts.filter((toast) => toast.id !== id));

        if (type === ToastType.SIMPLE) {
          return (
            <SimpleToast
              key={id}
              message={message}
              duration={duration}
              onClose={handleClose}
            />
          );
        }

        return (
          <Toast
            key={id}
            message={message}
            type={type}
            duration={duration}
            buttonProps={buttonProps}
            onClose={handleClose}
          />
        );
      })}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
